<template>
  <div class="privacidade-termos">
    <!-- Header -->
    <div class="header">
      <div class="header-main-layout">
        <div class="header-content-container">
          <!-- Mobile Menu Button -->
          <v-btn
            icon
            @click="mobileDrawer = true"
            class="mobile-menu-btn mobile-only"
          >
            <v-icon>mdi-menu</v-icon>
          </v-btn>

          <h1 class="header-title">{{ data.title }}</h1>

          <div class="desktop-search">
            <v-text-field
              v-model="searchTerm"
              :placeholder="data.searchPlaceholder"
              hide-details
              solo
              dense
              prepend-inner-icon="mdi-magnify"
              class="search-input"
            ></v-text-field>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Navigation Drawer -->
    <v-navigation-drawer
      v-model="mobileDrawer"
      temporary
      app
      class="mobile-drawer"
      width="280"
    >
      <div class="mobile-drawer-content">
        <!-- Mobile Search -->
        <div class="mobile-search-container">
          <v-text-field
            v-model="searchTerm"
            :placeholder="data.searchPlaceholder"
            hide-details
            solo
            dense
            prepend-inner-icon="mdi-magnify"
            class="mobile-search-input"
          ></v-text-field>
        </div>

        <!-- Mobile Menu Items -->
        <div class="mobile-menu-list">
          <div
            v-for="item in data.menu"
            :key="item.id"
            :class="['mobile-menu-item', { active: activeSection === item.id }]"
            @click="setActiveSectionMobile(item.id)"
          >
            {{ item.label }}
          </div>
        </div>
      </div>
    </v-navigation-drawer>

    <!-- Main Layout -->
    <div class="main-layout">
      <!-- Menu Lateral -->
      <div class="sidebar">
        <div class="menu-list">
          <div
            v-for="item in data.menu"
            :key="item.id"
            :class="['menu-item', { active: activeSection === item.id }]"
            @click="setActiveSection(item.id)"
          >
            {{ item.label }}
          </div>
        </div>
      </div>

      <!-- Conteúdo Principal -->
      <div class="content-area">
        <div class="content-container">
          <!-- Modo Normal (sem busca) -->
          <div v-if="!searchTerm">
            <div
              v-for="(section, index) in currentContent.sections"
              :key="index"
              class="content-section"
            >
              <h3 v-if="section.title" class="section-title">
                {{ section.title }}
              </h3>
              <div v-if="section.content" class="section-text">
                <p
                  v-for="(paragraph, pIndex) in getParagraphs(section.content)"
                  :key="pIndex"
                  class="paragraph"
                  v-html="paragraph"
                ></p>
              </div>
              <ul v-if="section.list" class="section-list">
                <li
                  v-for="(listItem, listIndex) in section.list"
                  :key="listIndex"
                  v-html="formatListItem(listItem)"
                ></li>
              </ul>

              <!-- Conteúdo após a lista -->
              <div v-if="section.content_after_list" class="section-text">
                <p
                  v-for="(paragraph, pIndex) in getParagraphs(
                    section.content_after_list
                  )"
                  :key="pIndex"
                  class="paragraph"
                  v-html="paragraph"
                ></p>
              </div>

              <!-- Seções aninhadas -->
              <div v-if="section.sections" class="nested-sections">
                <div
                  v-for="(nestedSection, nestedIndex) in section.sections"
                  :key="nestedIndex"
                  class="nested-section"
                >
                  <h4 v-if="nestedSection.title" class="nested-section-title">
                    {{ nestedSection.title }}
                  </h4>
                  <div v-if="nestedSection.content" class="nested-section-text">
                    <p
                      v-for="(paragraph, pIndex) in getParagraphs(
                        nestedSection.content
                      )"
                      :key="pIndex"
                      class="paragraph"
                      v-html="paragraph"
                    ></p>
                  </div>
                  <ul v-if="nestedSection.list" class="nested-section-list">
                    <li
                      v-for="(
                        nestedListItem, nestedListIndex
                      ) in nestedSection.list"
                      :key="nestedListIndex"
                      v-html="formatListItem(nestedListItem)"
                    ></li>
                  </ul>

                  <!-- Conteúdo após a lista (seções aninhadas) -->
                  <div
                    v-if="nestedSection.content_after_list"
                    class="nested-section-text"
                  >
                    <p
                      v-for="(paragraph, pIndex) in getParagraphs(
                        nestedSection.content_after_list
                      )"
                      :key="pIndex"
                      class="paragraph"
                      v-html="paragraph"
                    ></p>
                  </div>

                  <!-- Tabela (seções aninhadas) -->
                  <div v-if="nestedSection.table" class="nested-section-table">
                    <v-simple-table class="data-table">
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th
                              v-for="(header, headerIndex) in nestedSection
                                .table.headers"
                              :key="headerIndex"
                              class="table-header"
                            >
                              {{ header }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(row, rowIndex) in nestedSection.table.rows"
                            :key="rowIndex"
                            class="table-row"
                          >
                            <td v-if="row.finalidade" class="table-cell">
                              {{ row.finalidade }}
                            </td>
                            <td v-if="row.tipos_dados" class="table-cell">
                              {{ row.tipos_dados }}
                            </td>
                            <td v-if="row.como_coletado" class="table-cell">
                              {{ row.como_coletado }}
                            </td>
                            <td v-if="row.nome_cookie" class="table-cell">
                              {{ row.nome_cookie }}
                            </td>
                            <td v-if="row.tempo_vencimento" class="table-cell">
                              {{ row.tempo_vencimento }}
                            </td>
                            <td v-if="row.descricao" class="table-cell">
                              {{ row.descricao }}
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </div>
                </div>
              </div>

              <!-- Tabela (seções principais) -->
              <div v-if="section.table" class="section-table">
                <v-simple-table class="data-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th
                          v-for="(header, headerIndex) in section.table.headers"
                          :key="headerIndex"
                          class="table-header"
                        >
                          {{ header }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(row, rowIndex) in section.table.rows"
                        :key="rowIndex"
                        class="table-row"
                      >
                        <td v-if="row.finalidade" class="table-cell">
                          {{ row.finalidade }}
                        </td>
                        <td v-if="row.tipos_dados" class="table-cell">
                          {{ row.tipos_dados }}
                        </td>
                        <td v-if="row.como_coletado" class="table-cell">
                          {{ row.como_coletado }}
                        </td>
                        <td v-if="row.nome_cookie" class="table-cell">
                          {{ row.nome_cookie }}
                        </td>
                        <td v-if="row.tempo_vencimento" class="table-cell">
                          {{ row.tempo_vencimento }}
                        </td>
                        <td v-if="row.descricao" class="table-cell">
                          {{ row.descricao }}
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </div>
          </div>

          <!-- Modo Busca -->
          <div v-else>
            <h2 class="content-title">
              Resultados da busca: "{{ searchTerm }}"
            </h2>
            <div class="content-divider"></div>

            <!-- Sem resultados -->
            <div v-if="searchResults.length === 0" class="no-results">
              <div class="no-results-icon">🔍</div>
              <h3 class="no-results-title">Nenhum resultado encontrado</h3>
              <p class="no-results-text">
                Tente usar palavras-chave diferentes ou verifique a ortografia.
              </p>
            </div>

            <!-- Com resultados -->
            <div v-else>
              <div
                v-for="(result, index) in searchResults"
                :key="index"
                class="search-result"
              >
                <div class="result-source">
                  <span class="result-badge">{{ result.sourceName }}</span>
                  <span v-if="result.parentSection" class="parent-section">
                    › {{ result.parentSection }}</span
                  >
                </div>
                <h3 v-if="result.section.title" class="section-title">
                  {{ result.section.title }}
                </h3>
                <div v-if="result.section.content" class="section-text">
                  <p
                    v-for="(paragraph, pIndex) in getParagraphs(
                      result.section.content
                    )"
                    :key="pIndex"
                    class="paragraph"
                    v-html="highlightSearchTerm(paragraph)"
                  ></p>
                </div>
                <ul v-if="result.section.list" class="section-list">
                  <li
                    v-for="(listItem, listIndex) in result.section.list"
                    :key="listIndex"
                    v-html="highlightSearchTerm(formatListItem(listItem))"
                  ></li>
                </ul>

                <!-- Conteúdo após a lista (busca) -->
                <div
                  v-if="result.section.content_after_list"
                  class="section-text"
                >
                  <p
                    v-for="(paragraph, pIndex) in getParagraphs(
                      result.section.content_after_list
                    )"
                    :key="pIndex"
                    class="paragraph"
                    v-html="highlightSearchTerm(paragraph)"
                  ></p>
                </div>

                <!-- Tabela (busca) -->
                <div v-if="result.section.table" class="section-table">
                  <v-simple-table class="data-table">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th
                            v-for="(header, headerIndex) in result.section.table
                              .headers"
                            :key="headerIndex"
                            class="table-header"
                          >
                            {{ header }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(row, rowIndex) in result.section.table.rows"
                          :key="rowIndex"
                          class="table-row"
                        >
                          <td v-if="row.finalidade" class="table-cell">
                            {{ row.finalidade }}
                          </td>
                          <td v-if="row.tipos_dados" class="table-cell">
                            {{ row.tipos_dados }}
                          </td>
                          <td v-if="row.como_coletado" class="table-cell">
                            {{ row.como_coletado }}
                          </td>
                          <td v-if="row.nome_cookie" class="table-cell">
                            {{ row.nome_cookie }}
                          </td>
                          <td v-if="row.tempo_vencimento" class="table-cell">
                            {{ row.tempo_vencimento }}
                          </td>
                          <td v-if="row.descricao" class="table-cell">
                            {{ row.descricao }}
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer com data de atualização -->
          <div class="content-footer">
            <div class="last-updated">Última atualização: 22/09/2025</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import privacidadeTermosData from './privacidadeTermos.json'

export default {
  name: 'PrivacidadeTermos',
  data() {
    return {
      data: privacidadeTermosData,
      activeSection: this.getInitialSection(),
      searchTerm: '',
      mobileDrawer: false,
    }
  },
  computed: {
    currentContent() {
      return this.data.content[this.activeSection]
    },
    searchResults() {
      if (!this.searchTerm) {
        return []
      }

      const searchLower = this.searchTerm.toLowerCase()
      const results = []

      // Buscar em todas as seções (Política de Privacidade e Termos de Uso)
      Object.keys(this.data.content).forEach((sectionKey) => {
        const sectionData = this.data.content[sectionKey]
        const sectionName =
          this.data.menu.find((item) => item.id === sectionKey)?.label ||
          sectionKey

        sectionData.sections.forEach((section) => {
          const titleMatch =
            section.title && section.title.toLowerCase().includes(searchLower)
          const contentMatch =
            section.content &&
            section.content.toLowerCase().includes(searchLower)
          const contentAfterListMatch =
            section.content_after_list &&
            section.content_after_list.toLowerCase().includes(searchLower)
          const listMatch =
            section.list &&
            section.list.some((item) =>
              item.toLowerCase().includes(searchLower)
            )

          if (
            titleMatch ||
            contentMatch ||
            contentAfterListMatch ||
            listMatch
          ) {
            results.push({
              section: section,
              sourceKey: sectionKey,
              sourceName: sectionName,
            })
          }

          // Buscar também nas seções aninhadas
          if (section.sections) {
            section.sections.forEach((nestedSection) => {
              const nestedTitleMatch =
                nestedSection.title &&
                nestedSection.title.toLowerCase().includes(searchLower)
              const nestedContentMatch =
                nestedSection.content &&
                nestedSection.content.toLowerCase().includes(searchLower)
              const nestedContentAfterListMatch =
                nestedSection.content_after_list &&
                nestedSection.content_after_list
                  .toLowerCase()
                  .includes(searchLower)
              const nestedListMatch =
                nestedSection.list &&
                nestedSection.list.some((item) =>
                  item.toLowerCase().includes(searchLower)
                )

              if (
                nestedTitleMatch ||
                nestedContentMatch ||
                nestedContentAfterListMatch ||
                nestedListMatch
              ) {
                results.push({
                  section: nestedSection,
                  sourceKey: sectionKey,
                  sourceName: sectionName,
                  parentSection: section.title,
                })
              }
            })
          }
        })
      })

      return results
    },
  },
  methods: {
    getParagraphs(content) {
      if (!content) return []
      return content
        .split('\n\n')
        .filter((paragraph) => paragraph.trim() !== '')
    },
    formatListItem(item) {
      // Processar itens com numeração romana (I -, II -, III -, etc.)
      return item
        .replace(/^([IVX]+)\s*-\s*([^:]+):/, '<strong>$1 - $2:</strong>')
        .replace(/^([a-z])\s*\)\s*([^:]+):/, '<strong>$1) $2:</strong>')
        .replace(/^([ivx]+)\s*-\s*([^;]+);/, '<strong>$1 - $2;</strong>')
    },
    setActiveSection(sectionId) {
      this.activeSection = sectionId
      this.searchTerm = '' // Sempre limpar a busca ao trocar de seção
    },
    setActiveSectionMobile(sectionId) {
      this.activeSection = sectionId
      this.searchTerm = '' // Sempre limpar a busca ao trocar de seção
      this.mobileDrawer = false // Fechar drawer no mobile
    },
    highlightSearchTerm(text) {
      if (!this.searchTerm || !text) {
        return text
      }

      const searchRegex = new RegExp(
        `(${this.escapeRegex(this.searchTerm)})`,
        'gi'
      )
      return text.replace(
        searchRegex,
        '<mark class="search-highlight">$1</mark>'
      )
    },
    escapeRegex(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    },
    getInitialSection() {
      // Determina a seção inicial baseada na rota atual
      const currentPath = this.$route.path
      if (currentPath === '/termos-uso') {
        return 'termos-uso'
      }
      return 'politica-privacidade' // padrão para /privacidade-termos
    },
    setActiveSection(sectionId) {
      this.activeSection = sectionId
      this.searchTerm = '' // Limpar busca ao trocar de seção

      // Navegar para a rota correspondente
      if (sectionId === 'termos-uso') {
        this.$router.push('/termos-uso')
      } else {
        this.$router.push('/privacidade')
      }

      // Rolar para o topo do conteúdo
      this.$nextTick(() => {
        const contentArea = document.querySelector('.content-area')
        if (contentArea) {
          contentArea.scrollTop = 0
        }
      })
    },
    setActiveSectionMobile(sectionId) {
      this.setActiveSection(sectionId)
      this.mobileDrawer = false // Fechar drawer no mobile
    },
  },
  watch: {
    $route(to) {
      this.activeSection = this.getInitialSection()
      this.searchTerm = '' // Limpar busca ao trocar de rota

      // Rolar para o topo do conteúdo ao trocar de rota
      this.$nextTick(() => {
        const contentArea = document.querySelector('.content-area')
        if (contentArea) {
          contentArea.scrollTop = 0
        }
      })
    },
  },
}
</script>

<style scoped>
.privacidade-termos {
  height: 100vh;
  background-color: transparent;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Header */
.header {
  background-color: #fff;
  padding: 0;
  border-bottom: 1px solid #e1e5e9;
  position: sticky;
  top: 0;
  z-index: 1000;
}

.header-main-layout {
  display: flex;
  max-width: 1400px;
  margin: 0 10%;
  height: 65px;
}

.header-content-container {
  width: 100%;
  padding: 20px 20px 20px 20px !important;
  display: flex;
  align-items: center;
  gap: 20px;
  padding-left: 0;
}

.header-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  flex: 1;
}

.mobile-menu-btn {
  color: #4472c4 !important;
  flex-shrink: 0;
}

.mobile-only {
  display: none !important;
}

.desktop-only {
  display: block;
}

.desktop-search {
  flex-shrink: 0;
}

.search-input {
  width: 300px;
}

.main-layout {
  display: flex;
  max-width: 1400px;
  margin: 0 10%;
  flex: 1;
  overflow: hidden;
}

.sidebar {
  width: 280px;
  background-color: #fff;
  /* border-right: 1px solid #e1e5e9; */
  position: relative;
  z-index: 1;
  height: 100%;
  overflow-y: auto;
  flex-shrink: 0;
}

.menu-list {
  padding: 0;
  margin-top: 20px;
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  color: #495057;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  /* border-bottom: 1px solid #f1f3f4; */
  transition: all 0.2s ease;
}

.menu-item:hover {
  background-color: transparent;
  color: #4472c4;
}

.menu-item.active {
  color: #4472c4;
  font-weight: 600;
  background-color: transparent;
}

.content-area {
  flex: 1;
  background-color: #fff;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  max-width: 100%;
}

.content-container {
  padding: 40px 20px 40px 20px;
  max-width: 900px;
  flex: 1;
  min-height: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
}

.content-section {
  margin-bottom: 32px;
  background-color: transparent !important;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  max-width: 100%;
  overflow: hidden;
}

.section-title {
  color: #212529;
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 16px;
  background-color: transparent !important;
  padding-left: 0px !important;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
}

.section-text {
  color: #495057;
  line-height: 1.7;
  font-size: 16px;
  text-align: justify;
  margin-bottom: 16px;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  white-space: normal;
  max-width: 100%;
  overflow: hidden;
}

.paragraph {
  margin-bottom: 16px;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  white-space: normal;
  max-width: 100%;
  overflow: hidden;
}

.paragraph:last-child {
  margin-bottom: 0;
}

.section-list {
  color: #495057;
  line-height: 1.7;
  font-size: 16px;
  margin: 16px 0;
  padding-left: 24px;
}

.section-list li {
  margin-bottom: 8px;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}

.nested-sections {
  margin-top: 20px;
  background-color: transparent !important;
}

.nested-section {
  margin-bottom: 24px;
  background-color: transparent !important;
}

.nested-section-title {
  color: #212529;
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 12px 0;
}

.nested-section-text {
  color: #495057;
  line-height: 1.7;
  font-size: 16px;
  margin: 0 0 16px 0;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}

.nested-section-list {
  margin: 16px 0;
  padding-left: 24px;
}

.nested-section-list li {
  margin-bottom: 8px;
  line-height: 1.6;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}

/* Tabelas */
.section-table,
.nested-section-table {
  margin: 20px 0;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.table-header {
  background-color: #f5f5f5;
  color: #000000 !important;
  font-weight: 700;
  font-size: 14px;
  padding: 16px 12px;
  text-align: left;
  border: none;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-row {
  border-bottom: 1px solid #e1e5e9;
  transition: background-color 0.2s ease;
}

.table-row:hover {
  background-color: #f8f9fa;
}

.table-row:last-child {
  border-bottom: none;
}

.table-cell {
  padding: 16px 12px;
  font-size: 14px;
  line-height: 1.5;
  color: #495057;
  border: none;
  vertical-align: middle;
}

.table-cell:first-child {
  font-weight: 600;
  color: #212529;
  min-width: 150px;
  width: 20%;
}

/* Largura específica para a terceira coluna */
.table-cell:nth-child(3) {
  width: 40%;
  max-width: 300px;
  min-width: 200px;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
}

/* Forçar quebra de linha em todos os elementos de texto */
.content-section * {
  overflow-wrap: break-word !important;
  word-wrap: break-word !important;
  word-break: break-word !important;
  max-width: 100% !important;
  white-space: normal !important;
}

/* Footer com data de atualização */
.content-footer {
  margin-top: 80px;
  padding: 20px 0 20px 0;
  border-top: 1px solid #e1e5e9;
}

.last-updated {
  color: #6c757d;
  font-size: 14px;
  font-style: italic;
  text-align: center;
}

/* Search Results */
.no-results {
  text-align: center;
  padding: 60px 20px;
}

.no-results-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.3;
}

.no-results-title {
  color: #495057;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
}

.no-results-text {
  color: #6c757d;
  font-size: 14px;
}

.search-result {
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e9ecef;
}

.search-result:last-child {
  border-bottom: none;
}

.result-source {
  margin-bottom: 12px;
}

.result-badge {
  background: linear-gradient(135deg, #4472c4 0%, #365ba3 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.parent-section {
  color: #6c757d;
  font-size: 12px;
  font-weight: 500;
  margin-left: 8px;
}

.search-highlight {
  background-color: #fff3cd;
  color: #856404;
  padding: 2px 4px;
  border-radius: 3px;
  font-weight: 600;
}

/* Mobile Drawer */
.mobile-drawer {
  z-index: 2000 !important;
}

.mobile-drawer-content {
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.mobile-search-container {
  padding: 20px;
  border-bottom: 1px solid #e1e5e9;
  background-color: transparent;
}

.mobile-search-input {
  margin-bottom: 0;
}

.mobile-menu-list {
  flex: 1;
  padding: 0;
}

.mobile-menu-item {
  display: flex;
  align-items: center;
  padding: 18px 24px;
  color: #495057;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  border-bottom: 1px solid #f1f3f4;
  transition: all 0.2s ease;
  background-color: white;
}

.mobile-menu-item:hover {
  background-color: transparent;
  color: #4472c4;
}

.mobile-menu-item.active {
  color: #4472c4;
  font-weight: 600;
  background-color: transparent;
}

@media (max-width: 1200px) {
  .header-main-layout {
    margin: 0 auto;
  }

  .main-layout {
    margin: 0 auto;
  }

  .header-content-container {
    padding: 0 30px;
    padding-left: 0;
  }

  .content-container {
    padding: 30px 30px 30px 50px;
  }
}

@media (max-width: 768px) {
  .mobile-only {
    display: block !important;
  }

  .desktop-only,
  .desktop-search {
    display: none !important;
  }

  .header-main-layout {
    padding: 0 20px;
  }

  .header-content-container {
    padding: 0;
    justify-content: center;
  }

  .header-title {
    font-size: 20px;
    text-align: center;
  }

  .sidebar {
    display: none !important;
  }

  .main-layout {
    display: block;
    flex: 1;
    overflow: hidden;
    margin: 0 0px !important;
  }

  .content-area {
    width: 100%;
    max-width: none;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    box-sizing: border-box;
  }

  .content-container {
    padding: 24px 20px;
    max-width: 100%;
    overflow-x: hidden;
  }

  .section-text,
  .nested-section-text,
  .paragraph {
    text-align: left;
    font-size: 15px;
    line-height: 1.6;
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    white-space: normal !important;
    max-width: 100% !important;
    overflow: hidden !important;
    box-sizing: border-box;
  }

  .section-title,
  .nested-section-title {
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
  }

  /* Forçar quebra de linha em todos os elementos em mobile */
  .content-section * {
    overflow-wrap: break-word !important;
    word-wrap: break-word !important;
    word-break: break-word !important;
    max-width: 100% !important;
    white-space: normal !important;
    box-sizing: border-box !important;
  }

  .section-title {
    font-size: 20px;
  }

  .nested-section-title {
    font-size: 16px;
  }

  .mobile-search-container {
    padding: 16px;
  }

  .mobile-menu-item {
    padding: 16px 20px;
  }
}

@media (max-width: 480px) {
  .header-main-layout {
    padding: 0 16px;
  }

  .header-title {
    font-size: 18px;
  }

  .content-container {
    padding: 20px 16px;
  }

  .content-title {
    font-size: 24px;
  }

  .section-title {
    font-size: 18px;
  }

  .section-text,
  .section-list {
    font-size: 14px;
  }

  /* Tabelas responsivas */
  .section-table,
  .nested-section-table {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 20px -20px;
    padding: 0 20px;
  }

  .data-table {
    font-size: 12px;
    min-width: 650px;
    max-width: 800px;
    width: auto;
  }

  .table-header,
  .table-cell {
    padding: 12px 8px;
    white-space: nowrap;
  }

  .table-header:first-child,
  .table-cell:first-child {
    min-width: 120px;
    max-width: 150px;
  }

  .table-header:nth-child(2),
  .table-cell:nth-child(2) {
    min-width: 80px;
    max-width: 100px;
  }

  .table-header:nth-child(3),
  .table-cell:nth-child(3) {
    min-width: 150px;
    max-width: 200px;
    white-space: normal;
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  .table-header:nth-child(4),
  .table-cell:nth-child(4) {
    min-width: 80px;
    max-width: 100px;
  }

  .table-header:nth-child(5),
  .table-cell:nth-child(5) {
    min-width: 80px;
    max-width: 100px;
  }

  .table-header:nth-child(6),
  .table-cell:nth-child(6) {
    min-width: 100px;
    max-width: 150px;
    white-space: normal;
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  /* Footer responsivo */
  .content-footer {
    margin-top: 60px;
    padding: 30px 0 40px 0;
  }

  .last-updated {
    font-size: 13px;
  }

  /* Smaller mobile drawer adjustments */
  .mobile-search-container {
    padding: 12px;
  }

  .mobile-menu-item {
    padding: 14px 16px;
    font-size: 14px;
  }
}
</style>
